import type { SelectProps } from 'antd/es/select';
import { currencyToCountryMap } from 'shared/src/currency-to-countries';

import { hasValue } from 'utils/hasValue';

import type { GetFxMarkups200DataItem } from 'api/customerApi';

type GetCurrencyOptionsParams = {
  tradedCurrency?: string;
  markupData: GetFxMarkups200DataItem[];
  currencyOptions: SelectProps['options'];
  currencies?: SelectProps['options'];
  filterType: 'pay' | 'buy';
};

/**
 * Internal use only (do not export):
 *
 * This function is used to generate the list of the currency that you're trading for. It acts like a filter for the currency that you are trading.
 *
 * example: If I decide to pay in USD, what are the currencies that I can buy?
 * example: If I decide to purchase some AUD, what are the currencies that I can use for that?
 *
 * Input: based on the filterType, can be 'pay' or 'buy'
 *
 * Output: buy currency -> and deduces a list of pay currencies
 * Output: pay currency -> and deduces a list of buy currencies
 *
 */
const getCurrencyOptions = ({
  tradedCurrency = undefined,
  markupData,
  currencyOptions,
  currencies,
  filterType
}: GetCurrencyOptionsParams) => {
  if (!hasValue(currencyOptions)) {
    return [];
  }

  if (!hasValue(tradedCurrency)) {
    return currencyOptions.some((option) => option.value === 'ANY')
      ? currencies
      : currencyOptions;
  }

  const canTradeFromAnything = markupData.some((markup) =>
    filterType === 'buy'
      ? (markup.buy_currency_iso === tradedCurrency &&
          markup.pay_currency_iso === 'ANY') ||
        (markup.buy_currency_iso === 'ANY' && markup.pay_currency_iso === 'ANY')
      : (markup.pay_currency_iso === tradedCurrency &&
          markup.buy_currency_iso === 'ANY') ||
        (markup.pay_currency_iso === 'ANY' && markup.buy_currency_iso === 'ANY')
  );

  if (canTradeFromAnything) {
    return currencies?.filter((currency) => currency.value !== tradedCurrency);
  }

  return currencyOptions
    .filter((option) => option.value !== tradedCurrency)
    .filter((option) => {
      const hasMatchingPair = markupData?.some((markup) =>
        filterType === 'buy'
          ? (markup.buy_currency_iso === tradedCurrency ||
              markup.buy_currency_iso === 'ANY') &&
            markup.pay_currency_iso === option.value
          : (markup.pay_currency_iso === tradedCurrency ||
              markup.pay_currency_iso === 'ANY') &&
            markup.buy_currency_iso === option.value
      );
      return hasMatchingPair;
    });
};

// Usage for pays
export const getPaysOptions = ({
  tradedCurrency,
  markupData,
  payOptions,
  currencies
}: Omit<GetCurrencyOptionsParams, 'filterType' | 'currencyOptions'> & {
  payOptions: SelectProps['options'];
}) => {
  return getCurrencyOptions({
    tradedCurrency,
    markupData,
    currencyOptions: payOptions,
    currencies: currencies ?? getAllCurrenciesOptions(),
    filterType: 'buy'
  });
};

// Usage for buys
export const getBuysOptions = ({
  tradedCurrency,
  markupData,
  buyOptions,
  currencies
}: Omit<GetCurrencyOptionsParams, 'filterType' | 'currencyOptions'> & {
  buyOptions: SelectProps['options'];
}) => {
  return getCurrencyOptions({
    tradedCurrency,
    markupData,
    currencyOptions: buyOptions,
    currencies: currencies ?? getAllCurrenciesOptions(),
    filterType: 'pay'
  });
};

/**
 * Transforms a mapping of currency ISO codes to country codes into an array of currency options.
 *
 * The input mapping might look like this:
 *
 * const currencyToCountryMap = {
 *   AFN: ['AF'],
 *   ALL: ['AL']
 * };
 *
 * This function converts it into an array of objects formatted as:
 * [
 *   {
 *     "value": "AFN",
 *     "label": "AFN"
 *   },
 *   {
 *     "value": "ALL",
 *     "label": "ALL"
 *   }
 * ]
 * @returns An array of objects where each object represents a currency option with `value` and `label` fields.
 */
export function getAllCurrenciesOptions() {
  return Object.entries(currencyToCountryMap).flatMap(([currency]) => {
    return { value: currency, label: currency };
  });
}
